import React, { Suspense, lazy, useState } from 'react';
import { motion } from 'framer-motion';
import { use3DTier } from '../../lib/site';
import { useNearViewport } from '../../hooks/useNearViewport';

const WallRoofScene = lazy(() => import('../three/WallRoofScene'));

const MODES = [
  { key: 'wall', label: 'WALL SYSTEM' },
  { key: 'roof', label: 'ROOF SYSTEM' },
  { key: 'complete', label: 'COMPLETE SYSTEM' },
];

const Fallback = () => (
  <div className="w-full h-full rounded-xl border border-border bg-[#FBF8F3] flex items-center justify-center">
    <p className="font-mono-tech text-xs tracking-widest uppercase text-muted-foreground">Integrated wall + roof envelope</p>
  </div>
);

export const WallRoofSection = () => {
  const [mode, setMode] = useState('complete');
  const tier = use3DTier();
  // Mount the 3D canvas only when the user scrolls near the section.
  const [containerRef, near] = useNearViewport();

  return (
    <section id="wallroof" className="py-16 sm:py-20 lg:py-28 bg-[#FBF8F3] border-y border-border" data-testid="wallroof-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          className="max-w-2xl"
        >
          <p className="font-mono-tech text-xs tracking-[0.28em] uppercase text-[#B87333]">02 — Integrated Envelope</p>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
            WALLS AND ROOF.
            <br />
            ONE INTEGRATED SYSTEM.
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Engineered wall and roof systems create a lighter, insulated and rapidly installable building envelope.
          </p>
        </motion.div>

        <div className="mt-10">
          <div className="inline-flex rounded-lg border border-border bg-background p-1" role="tablist" aria-label="System view">
            {MODES.map((m) => (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={mode === m.key}
                data-testid={`wall-roof-toggle-${m.key}`}
                onClick={() => setMode(m.key)}
                className={`px-4 sm:px-5 py-2.5 rounded-md font-mono-tech text-[11px] sm:text-xs tracking-[0.14em] transition-[background-color,color] ${
                  mode === m.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div ref={containerRef} className="mt-6 h-[360px] sm:h-[460px]" data-testid="wallroof-3d-container">
            {tier === 'full' && near ? (
              <Suspense fallback={<Fallback />}>
                <WallRoofScene mode={mode} />
              </Suspense>
            ) : (
              <Fallback />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
